import React, { createContext, useContext, useMemo } from 'react';
import { useGlobalTheme } from '../hooks/useSectionAccess';

type ThemeVariant =
  | 'standard'
  | 'glass'
  | 'pastel'
  | 'earth'
  | 'sage'
  | 'ocean'
  | 'white'
  | 'black'
  | 'sunset'
  | 'forest'
  | 'midnight'
  | 'aurora'
  | 'amber'
  | 'emerald'
  | 'premium'
  | 'slate-blue'
  | 'mui-official'
  | 'warm-success'
  | 'midnight-luxe'
  | 'aurora-fresh'
  | 'neo-tokyo'
  | 'sakura'
  | 'monochrome-pro'
  | 'terracotta'
  | 'nordic-frost'
  | string;

interface ThemeVariantContextType {
  variant: ThemeVariant;
  colorMode: 'light' | 'dark';
  loading: boolean;
  setVariant: (variant: ThemeVariant) => void; // No-op for backward compatibility
}

const ThemeVariantContext = createContext<ThemeVariantContextType>({
  variant: 'warm-success',
  colorMode: 'light',
  loading: true,
  setVariant: () => {
    // Theme is now controlled globally by admin. User cannot change theme.
  },
});

export const useThemeVariant = () => useContext(ThemeVariantContext);

/**
 * ThemeVariantProvider - Provides global theme from Firestore admin configuration
 *
 * Theme is controlled centrally by administrators through the admin panel.
 * All users see the same theme (warm-success by default).
 * localStorage is no longer used for theme storage.
 */
export const ThemeVariantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Get global theme from Firestore appConfiguration
  const { globalTheme, colorMode, loading } = useGlobalTheme();

  // Set DOM attribute for CSS-in-JS access
  React.useEffect(() => {
    if (!loading) {
      document.documentElement.setAttribute('data-theme-variant', globalTheme);
      document.documentElement.setAttribute('data-color-mode', colorMode);
    }
  }, [globalTheme, colorMode, loading]);

  // setVariant is now a no-op - theme is controlled by admin only
  const setVariant = React.useCallback((_variant: ThemeVariant) => {
    // Theme is now controlled globally by admin. User cannot change theme.
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      variant: globalTheme as ThemeVariant,
      colorMode,
      loading,
      setVariant,
    }),
    [globalTheme, colorMode, loading, setVariant]
  );

  return <ThemeVariantContext.Provider value={value}>{children}</ThemeVariantContext.Provider>;
};
